/**
 * The name-anchor scheme: an element named `Door #needle` derives its deterministic id
 * from the text before the marker instead of (name, index, file hash) — so the id
 * survives content changes and asset re-uploads as long as the name stays.
 *
 * CONTRACT NOTE — the marker set is part of the id contract. Markers may be ADDED (new
 * names start anchoring), but never removed or re-prioritized, and every WRITER of ids
 * (this package's consumers: cloud upload, editors, CLI) must agree on the set before a
 * new marker ships — a writer that anchors on a marker another writer doesn't know mints
 * DIFFERENT ids for the same file. The anchored id only hashes the VALUE (not the
 * marker), so all markers are aliases into the same id space.
 */

/** the original marker — kept as a named export for existing consumers */
export const ANCHOR_MARKER = "#needle";

/**
 * All recognized markers, matched longest-first so overlapping suffixes resolve
 * deterministically. Currently one entry; see the contract note before adding more.
 */
export const ANCHOR_MARKERS: readonly string[] = [ANCHOR_MARKER];

export type ParsedAnchor = {
    /** the marker that matched (one of {@link ANCHOR_MARKERS}) */
    marker: string;
    /** the anchor value — the deterministic-id input */
    value: string;
};

/**
 * Parse an element name against all recognized markers (case-insensitive suffix match).
 * Returns null when no marker matches or the anchor value is empty.
 */
export function parseAnchor(rawName: string | null | undefined): ParsedAnchor | null {
    if (rawName == null) return null;
    const trimmed = rawName.trim();
    const lower = trimmed.toLowerCase();
    for (const marker of byLengthDescending(ANCHOR_MARKERS)) {
        if (!lower.endsWith(marker)) continue;
        const value = normalizeName(trimmed.substring(0, trimmed.length - marker.length));
        return value.length > 0 ? { marker, value } : null;
    }
    return null;
}

/**
 * Names treat SPACES and UNDERSCORES as the same character (runs collapse to one space):
 * three's GLTFLoader sanitizes node names on load (whitespace becomes "_"), so
 * "Door Frame #needle" appears live as "Door_Frame_#needle" — without normalization a
 * stamped anchor and the live anchor would never match. Applied at PARSE time, so id
 * computation and resolution agree from one change point. Accepted trade: names differing
 * only by space-vs-underscore are one name.
 *
 * Exported because id derivation applies the SAME rule to unanchored names (see
 * {@link import("./id.js").idNameOf}) — one normalization, one change point.
 */
export function normalizeName(value: string): string {
    return value.replace(/[\s_]+/g, " ").trim();
}

/**
 * Extract the anchor value from an element name, or null when the name carries no anchor.
 * Convenience over {@link parseAnchor} for callers that only need the id input.
 */
export function parseAnchorValue(rawName: string | null | undefined): string | null {
    return parseAnchor(rawName)?.value ?? null;
}

function byLengthDescending(markers: readonly string[]): readonly string[] {
    return markers.length > 1 ? [...markers].sort((a, b) => b.length - a.length) : markers;
}
